import sortBy from "lodash.sortby";
import { useMemo } from "react";

import {
  Avatar,
  IconButton,
  Key,
  SkeletonBlock,
  SkeletonText,
  Text,
  Tooltip,
  TooltipFocusableTrigger,
} from "@zapier/design-system";

import { useTranslations } from "@zapier/i18n";
import { useAccount } from "@zapier/client";
import styles from "./ConnectionShareModalList.module.css";

import { ConnectionShare, isAccountOwnerId } from "./utils";
import { ConnectionShareType } from "../types";

interface Props {
  isLoading: boolean;
  shares: ConnectionShare[];
  onRemove: ({
    id,
    shareType,
  }: {
    id: Key;
    shareType: ConnectionShareType;
  }) => void;
  disableShare: boolean;
}

export function ConnectionShareModalList({
  isLoading,
  onRemove,
  shares,
  disableShare,
}: Props) {
  const t = useTranslations("connection-share-modal-list");
  const { account } = useAccount();
  const accountOwnerId = account?.owner?.id;

  //  Sort members and teams/groups alphabetically in modal list, owner comes first
  const sortedShares = useMemo(() => {
    return sortBy(shares, (share: ConnectionShare) => {
      if (share.shareType === "owner") {
        return 0;
      }
      return share?.name;
    });
  }, [shares]);

  // Render skeleton loading state during initial load
  if (shares.length === 0 && isLoading) {
    return (
      <div>
        <ul className={styles.root}>
          <li>
            <div className={styles.loading}>
              <SkeletonBlock height={24} width={24} />
              <SkeletonText lines={1} size="body" />
            </div>
          </li>
        </ul>
      </div>
    );
  }

  return (
    <div>
      <ul className={styles.root}>
        {sortedShares.map(
          ({ id, name, memberCount, photoUrl, email, shareType }) => {
            const memberCountText = memberCount
              ? t("labels.member-count", { count: memberCount })
              : "";

            const nameText = String(id) === "all" ? t("labels.everyone") : name;
            const url = photoUrl;
            const description = email || memberCountText || "";
            const role = shareType === "owner" ? t("labels.owner") : "";

            const isItemLoading = isLoading || (!id && !name && !photoUrl);
            // The backend silently ignores removing the account owner, so don't offer it.
            const isAccountOwner =
              shareType === "member" && isAccountOwnerId(id, accountOwnerId);

            return (
              <li key={id}>
                {isItemLoading ? (
                  <div className={styles.loading}>
                    <SkeletonBlock height={24} width={24} />
                    <SkeletonText lines={1} size="body" />
                  </div>
                ) : (
                  <div>
                    <Avatar name={nameText || ""} url={url} />
                    <Text tag="p" type="Body1">
                      <strong>{nameText}</strong> - {description}
                      {role ? ` (${role})` : ""}
                    </Text>
                  </div>
                )}
                {shareType !== "owner" && !isAccountOwner && !disableShare && (
                  <Tooltip content={t("actions.remove")} placement="left">
                    <TooltipFocusableTrigger>
                      <IconButton
                        name="formX"
                        aria-label={t("actions.remove")}
                        size="small"
                        variant="ghost"
                        onPress={() => onRemove({ id, shareType })}
                      />
                    </TooltipFocusableTrigger>
                  </Tooltip>
                )}
              </li>
            );
          },
        )}
      </ul>
    </div>
  );
}
